import { Injectable } from "@angular/core";
import { head, last, replace, split } from "lodash-es";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { AppError, AppErrorType } from "../app-error/app-error.model";
import { CoreHttpClient } from "../http-client/http-client.service";

import { CustomerManagementConfigService } from "./customer-management.config";
import { MASKED_VALUE } from "./customer-management.const";
import {
	Customer,
	CustomerDetailSection,
	CustomerManagementService,
	EmailAvailability,
	ConfirmPegaCustomerProfileRequest,
	ConfirmCogaCustomerProfileRequest,
} from "./customer-management.model";

@Injectable()
export class CoreCustomerManagementService implements CustomerManagementService {
	constructor(
		private httpClient: CoreHttpClient,
		private config: CustomerManagementConfigService
	) {}

	private static splitPhoneNumber<T extends Partial<Customer>>(customer: T): T {
		if (!customer.subscriptions || !customer.subscriptions.phoneNumber) {
			return customer;
		}

		const phoneNumberParts = split(customer.subscriptions.phoneNumber, " ");

		customer.subscriptions.phoneExtension = replace(head(phoneNumberParts)!, "+", "");
		customer.subscriptions.phoneNumber = last(phoneNumberParts);

		return customer;
	}

	private static maskFields<T extends Partial<Customer>>(customer: T, isCensorshipEnabled: boolean): T {
		if (customer.address && isCensorshipEnabled) {
			customer.address.city = MASKED_VALUE;
			customer.address.street = MASKED_VALUE;
			customer.address.zipCode = MASKED_VALUE;
		}

		if (customer.subscriptions && isCensorshipEnabled) {
			customer.subscriptions.phoneNumber = MASKED_VALUE;
		}

		return customer;
	}

	getCustomer(): Observable<Customer> {
		return this.httpClient
			.get<Customer>({ url: this.config.getCustomerEndpoint() })
			.pipe(map(CoreCustomerManagementService.splitPhoneNumber))
			.pipe(
				map((customer) =>
					CoreCustomerManagementService.maskFields(customer, this.config.getIsCensorshipEnabled())
				)
			);
	}

	getCustomerDetail(section?: CustomerDetailSection): Observable<Partial<Customer>> {
		return this.httpClient
			.get<Partial<Customer>>({
				url: this.config.getCustomerEndpoint(),
				...(section && { searchParams: { section } }),
			})
			.pipe(
				map((response) => (section ? this.mapToPartialCustomer(section)(response) : response)),
				map(CoreCustomerManagementService.splitPhoneNumber)
			);
	}

	updateCustomer(customer: Partial<Customer>): Observable<Customer> {
		return this.httpClient
			.patch<Customer>({
				url: this.config.getCustomerEndpoint(),
				data: customer,
			})
			.pipe(map(CoreCustomerManagementService.splitPhoneNumber));
	}

	verifyToken(token: string): Observable<object> {
		return this.httpClient.patch({
			url: this.config.getVerifyTokenEndpoint(),
			searchParams: { token },
		});
	}

	/**
	 * @desc HTTP status codes:
	 * - 200 The email was found (and is thus unavailable).
	 * - 404 The email was not found (and is thus available)
	 * - 500 Unknown error
	 */
	checkEmailAvailability(email: string, oidcToken: string): Observable<EmailAvailability> {
		return this.httpClient
			.get({
				url: this.config.getEmailAvailabilityEndpoint(),
				searchParams: { email },
				headers: {
					OidcToken: oidcToken,
				},
			})
			.pipe(
				// 200 means that the email is found and thus available
				map(() => ({ email, isAvailable: false })),
				catchError((error: AppError) => {
					// 404 means that the email is not found and thus available
					if (error.type === AppErrorType.NotFound) {
						return of({
							email,
							isAvailable: true,
						});
					}

					return throwError(() => error);
				})
			);
	}

	unsubscribeFromNewsletter(token: string): Observable<object> {
		return this.httpClient.delete({
			url: this.config.getUnsubscribeFromNewsletterEndpoint(),
			searchParams: { token },
		});
	}

	acceptTermsAndConditions(token: string): Observable<object> {
		return this.httpClient.patch({
			url: this.config.getTermsAndConditionsEndpoint(),
			data: { token },
		});
	}

	acceptLatestTermsAndConditions(token: string): Observable<void> {
		return this.httpClient.patch({
			url: this.config.getAcceptTermsAndConditionsEndpoint(),
			data: { token },
		});
	}

	declineTermsAndConditions(token: string): Observable<object> {
		return this.httpClient.delete({
			url: this.config.getDeclineTermsAndConditionsEndpoint(),
			data: { token },
		});
	}

	acceptDepositTermsAndConditions(): Observable<Partial<Customer>> {
		return this.updateCustomer({
			legal: {
				depositTermsAndConditions: true,
			},
		}).pipe(map(CoreCustomerManagementService.splitPhoneNumber));
	}

	acceptPrivacyPolicy(token: string): Observable<void> {
		return this.httpClient.patch({
			url: this.config.getAcceptPrivacyPolicyEndpoint(),
			data: { token },
		});
	}

	acceptEntityLegalNotice(token: string): Observable<void> {
		return this.httpClient.patch({
			url: this.config.getAcceptEntityLegalNoticeEndpoint(),
			data: { token },
		});
	}

	updateProfileValidation(): Observable<void> {
		return this.httpClient.patch({
			url: this.config.getProfileValidationEndpoint(),
			data: {},
		});
	}

	confirmProfile(
		requestData: ConfirmPegaCustomerProfileRequest | ConfirmCogaCustomerProfileRequest,
		countryCode?: string
	): Observable<void> {
		const profileConfirmationEndpoint = this.config.getProfileConfirmationEndpoint(countryCode);

		return this.httpClient.patch({
			url: profileConfirmationEndpoint,
			data: requestData,
		});
	}

	private mapToPartialCustomer(section: CustomerDetailSection): (response: any) => Partial<Customer> {
		/**
		 * (response: Address) => ({ address: response })
		 * (response: Profile) => ({ profile: response })
		 */
		return (response: any): Partial<Customer> => ({ [section]: response });
	}
}
